import { useState, useEffect } from 'react'
import { db } from '../lib/db'
import ViewOnDesktopCard from '../components/ViewOnDesktopCard'

// ── Pure formatting helpers (exported for unit tests) ──────────────────────────

// TF Index / gross averages render to one decimal; null/blank shows an em dash.
export function fmtNum(v, digits = 1) {
  if (v === null || v === undefined || v === '') return '–'
  const n = parseFloat(v)
  return Number.isNaN(n) ? '–' : n.toFixed(digits)
}

// score-vs-par as a signed label: E / +3 / -2. Null when unknown.
export function fmtVsPar(v) {
  if (v === null || v === undefined || v === '') return null
  const n = parseInt(v, 10)
  if (Number.isNaN(n)) return null
  return n === 0 ? 'E' : n > 0 ? `+${n}` : `${n}`
}

export function vsParColor(v) {
  const n = parseInt(v, 10)
  if (Number.isNaN(n)) return 'var(--gray-300)'
  return n < 0 ? 'var(--blue-birdie)' : n > 0 ? 'var(--red)' : 'var(--gray-300)'
}

// Year of an ISO timestamp (UTC), or null.
export function yearOf(iso) {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d.getUTCFullYear()
}

// "Course · 2026" sub-label for the best-round tile. Degrades gracefully when a
// piece is missing.
export function courseYearSub(round) {
  if (!round) return null
  const y = yearOf(round.created_at)
  const name = round.course_name || null
  if (name && y) return `${name} · ${y}`
  return name || (y ? String(y) : null)
}

export function hasRounds(stats) {
  return !!stats && Number(stats.rounds_total) > 0
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function HistoryPage() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const res = await db('get_player_stats')
        if (alive) setStats(res.data || null)
      } catch (err) {
        console.error('Stats fetch error:', err)
        if (alive) setError(true)
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [])

  return (
    <div className="page">
      <div className="container">
        <div style={{ paddingTop: 20, paddingBottom: 12, borderBottom: '1px solid var(--green-mid)', marginBottom: 16 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--gold)', marginBottom: 2 }}>Your Game</div>
          <h1 style={{ fontSize: '1.6rem' }}>Stats</h1>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : error ? (
          <div className="card" style={{ textAlign: 'center', padding: 40 }}>
            <p className="text-muted">Couldn’t load your stats. Pull to refresh or try again later.</p>
          </div>
        ) : !hasRounds(stats) ? (
          <EmptyState />
        ) : (
          <StatsTiles stats={stats} />
        )}
      </div>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="card" style={{ textAlign: 'center', padding: 40 }}>
      <p style={{ fontWeight: 600, marginBottom: 6 }}>No completed rounds yet</p>
      <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
        Play and finish a round to start building your stats.
      </p>
    </div>
  )
}

// ── Tiles ──────────────────────────────────────────────────────────────────────

function IndexPair({ overall, attested }) {
  return (
    <div className="card" style={{ display: 'flex', padding: 0, overflow: 'hidden', marginBottom: 16 }}>
      {[
        { label: 'TF Index', value: fmtNum(overall) },
        { label: 'Attested', value: fmtNum(attested) },
      ].map((c, i) => (
        <div key={c.label} style={{ flex: 1, padding: '16px 12px', textAlign: 'center', borderLeft: i ? '1px solid var(--green-mid)' : 'none' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--gray-500)', marginBottom: 6 }}>{c.label}</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.9rem', fontWeight: 700, color: 'var(--gold)' }}>{c.value}</div>
        </div>
      ))}
    </div>
  )
}

function Tile({ label, value, sub, valueColor }) {
  return (
    <div className="card" style={{ padding: '14px 14px' }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gray-500)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.5rem', fontWeight: 700, color: valueColor || 'var(--cream)', lineHeight: 1.1 }}>{value}</div>
      {sub && <div className="text-xs text-muted" style={{ marginTop: 4 }}>{sub}</div>}
    </div>
  )
}

function StatsTiles({ stats }) {
  const best = stats.best_round
  const recent = stats.recent_round
  const bestVsPar = best ? fmtVsPar(best.score_vs_par) : null
  const recentVsPar = recent ? fmtVsPar(recent.score_vs_par) : null

  return (
    <div>
      <IndexPair overall={stats.tf_index_overall} attested={stats.tf_index_attested} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
        <Tile
          label="Rounds YTD"
          value={stats.rounds_ytd ?? 0}
          sub={`${stats.rounds_total ?? 0} lifetime`}
        />
        <Tile
          label="Index-eligible"
          value={stats.rounds_index_eligible ?? 0}
          sub="rounds counted"
        />
        <Tile
          label="Avg gross YTD"
          value={fmtNum(stats.avg_gross_ytd)}
          sub={`${fmtNum(stats.avg_gross)} all-time`}
        />
        <Tile
          label="Best round"
          value={best && best.total_score != null ? best.total_score : '–'}
          valueColor="var(--blue-birdie)"
          sub={best ? courseYearSub(best) : null}
        />
      </div>

      {recent && (
        <>
          <p className="text-xs text-muted text-mono" style={{ marginBottom: 8, textTransform: 'uppercase' }}>Most recent round</p>
          <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', marginBottom: 16 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{recent.course_name || 'Round'}</div>
              <div className="text-xs text-muted">{yearOf(recent.created_at) || ''}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.4rem', fontWeight: 700 }}>{recent.total_score != null ? recent.total_score : '–'}</div>
              {recentVsPar && <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.78rem', color: vsParColor(recent.score_vs_par) }}>{recentVsPar}</div>}
            </div>
          </div>
        </>
      )}

      {best && bestVsPar && (
        <p className="text-xs text-muted" style={{ marginBottom: 16 }}>
          Best round is {bestVsPar} vs par.
        </p>
      )}

      <ViewOnDesktopCard
        title="Full stats dashboard"
        description="Trends, format breakdowns, and course history are richer on the desktop dashboard."
        path="/dashboard"
        cta="View on desktop"
      />
    </div>
  )
}
